import {testIdProps} from '@github-ui/test-id-props'
import {useDebounce} from '@github-ui/use-debounce'
import {Box, Heading, Text, TextInput} from '@primer/react'
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'

import {validateWorkflowName} from '../../helpers/workflow-validation'
import {useAutomationGraph} from '../../state-providers/workflows/use-automation-graph'
import {useWorkflows} from '../../state-providers/workflows/use-workflows'
import {SEARCH_DEBOUNCE_DELAY_MS} from './helpers/search-constants'

export const WorkflowNameEditor = () => {
  const {workflowNames} = useWorkflows()
  const {workflowName, isEditing, setWorkflowName, setIsWorkflowValid, workflow} = useAutomationGraph()
  const [error, setError] = useState<string | undefined>()

  const inputRef = useRef<HTMLInputElement>(null)

  // Only focus on edit mode for first rendering
  useLayoutEffect(() => {
    if (isEditing) {
      inputRef?.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validate = useCallback(
    (name: string) => {
      const validationResult = validateWorkflowName(name, workflowNames)
      if (!validationResult.isValid) {
        setIsWorkflowValid(false)
        setError(validationResult.errorMessage)
      } else if (error) {
        setIsWorkflowValid(true)
        setError(undefined)
      }
    },
    [workflowNames, setIsWorkflowValid, error],
  )

  const debouncedValidation = useDebounce(validate, SEARCH_DEBOUNCE_DELAY_MS)

  const onWorkflowNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newWorkflowName = event.target.value
      setWorkflowName(newWorkflowName)
      debouncedValidation(newWorkflowName)
    },
    [debouncedValidation, setWorkflowName],
  )

  useEffect(() => {
    if (isEditing) {
      validate(workflow.name)
    } else if (!isEditing) {
      setIsWorkflowValid(true)
      setError(undefined)
    }
    // we only want to validate the initial workflow name when first entering edit mode
    // and reset the states when exiting edit mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  return (
    <>
      {!isEditing ? (
        <Heading
          as="h2"
          sx={{fontSize: 3, color: 'fg.default', wordBreak: 'break-word'}}
          id="workflow-name"
          {...testIdProps('workflow-name-editor-read-only')}
        >
          {workflowName}
        </Heading>
      ) : (
        <Box sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
          <TextInput
            ref={inputRef}
            name="workflowName"
            aria-label="Workflow name"
            value={workflowName}
            onChange={onWorkflowNameChange}
            validationStatus={error ? 'error' : undefined}
            sx={{fontWeight: 'bold'}}
            {...testIdProps('workflow-name-editor-input')}
          />
          {error && (
            <Text as="p" sx={{color: 'danger.fg'}} {...testIdProps('workflow-name-editor-error')}>
              {error}
            </Text>
          )}
        </Box>
      )}
    </>
  )
}

try{ WorkflowNameEditor.displayName ||= 'WorkflowNameEditor' } catch {}